import { zodResolver } from "@hookform/resolvers/zod";
import { HeaderForm } from "@/components/header-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SimpleAccordion } from "@/components/ui/simple-accordion";
import { useForm } from "react-hook-form";
import { ProjectViewFormData, projectViewFormSchema } from "./schema";
import { ProjectViewForm } from "./project-view-form";
import { ProjectCoverForm } from "./project-cover-form";
import { ProjectView as ProjectViewType } from "@/types/project";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useRegisterSocialProject, useUpdateProject, useGetProjectById } from "@/services/queries/useProject";
import { useLocation } from "wouter";
import { useUpdateOnboardingSteps } from "@/services/queries/useUsers";
import { useAuthContext } from "@/context/authentication/auth-context";

interface ProjectViewProps {
  onContinue?: () => void;
  onBack?: () => void;
  projectId?: number;
}

export function ProjectView({ onBack, projectId }: ProjectViewProps) {
  const [pending, setPending] = useState(false);

  const [, navigate] = useLocation();

  const { person } = useAuthContext()

  const form = useForm<ProjectViewFormData>({
    resolver: zodResolver(projectViewFormSchema),
    defaultValues: {
      presentationMaterialsLink: "",
      videoTeaserLink: "",
      coverImage: null,
    },
  });

  const { mutateAsync: updateOnboardingSteps } = useUpdateOnboardingSteps();
  const { mutateAsync: registerSocialProject } = useRegisterSocialProject(false);
  const { mutateAsync: updateProject } = useUpdateProject(false);
  const { data: project } = useGetProjectById(projectId || 0);

  useEffect(() => {
    if (project) {
      form.reset({
        presentationMaterialsLink: project.media_kit_url || "",
        videoTeaserLink: project.movie_teaser_url || "",
        coverImage: null
      });
      // Handle cover image preview if URL exists
      if (project.logo_url) {
        localStorage.setItem("coverImage", project.logo_url);
      }
    }
  }, [project, form]);

  const onFormSubmit = async (data: ProjectViewFormData) => {
    try {
      setPending(true);
      const projectViewData = ProjectViewType.mapToProjectView(data, projectId || 0);

      await updateProject(projectViewData)

      toast({
        title: "Dados salvos com sucesso",
        variant: "success",
      });

      await updateOnboardingSteps({ id_user: person?.id_user ?? 0, step: '8' })

      setTimeout(() => {
        navigate("/dashboard");
      }, 1000)
    } catch (error) {
      console.error("Erro ao salvar visual do projeto:", error);
      toast({
        title: "Erro ao salvar dados",
        variant: "destructive",
      });
    } finally {
      setPending(false);
    }
  };

  const accordionItems = [
    {
      name: "VISUAL DO PROJETO",
      component: <ProjectViewForm form={form} />,
    },
    {
      name: "CAPA DO PROJETO",
      component: <ProjectCoverForm form={form} />,
    },
  ]

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-8">
        <SimpleAccordion
          className="bg-darkblue rounded-lg p-8"
          itemClassName="border-none"
          items={accordionItems.map((item) => ({
            id: item.name,
            title: <HeaderForm title={item.name} />,
            content: item.component
          }))}
        />

        {/* Navigation Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            size={"sm"}
            variant="outline"
            disabled={pending}
            onClick={() => onBack?.()}
            children="Voltar"
            className="px-8"
          />

          <Button
            type="submit"
            size={"sm"}
            disabled={pending}
            isLoading={pending}
            children="Salvar & Continuar"
            className="px-8"
          />
        </div>
      </form>
    </Form>
  )
}